import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';

function detectSystemTheme(): 'light' | 'dark' {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

type AppState = {
  theme: 'light' | 'dark';
};

function initialState(): AppState {
  // Check localStorage first, fall back to system preference
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('app-theme');
    if (stored === 'light' || stored === 'dark') {
      return { theme: stored };
    }
  }
  return { theme: detectSystemTheme() };
}

export const AppStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, document = inject(DOCUMENT)) => {
    const htmlElement = document.documentElement;

    function applyTheme(theme: 'light' | 'dark'): void {
      if (theme === 'dark') {
        htmlElement.classList.add('dark');
      } else {
        htmlElement.classList.remove('dark');
      }
      localStorage.setItem('app-theme', theme);
    }

    return {
      setTheme(theme: 'light' | 'dark'): void {
        patchState(store, { theme });
        applyTheme(theme);
      },

      toggleTheme(): void {
        const next = store.theme() === 'light' ? 'dark' : 'light';
        patchState(store, { theme: next });
        applyTheme(next);
      },

      _initialize(): void {
        applyTheme(store.theme());

        if (typeof window !== 'undefined') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          mediaQuery.addEventListener('change', (event) => {
            // Only auto-switch if user hasn't manually set a preference
            const stored = localStorage.getItem('app-theme');
            if (!stored) {
              const next = event.matches ? 'dark' : 'light';
              patchState(store, { theme: next });
              applyTheme(next);
            }
          });
        }
      },
    };
  }),
  withHooks({
    onInit(store) {
      store._initialize();
    },
  }),
);
